import { findLastIndex, repeat } from './helpers';
import { parseMask } from './parse-mask';
export class MaskUtils {
  constructor(options) {
    this.maskOptions = void 0;
    this.isCharacterAllowedAtPosition = (character, position) => {
      var _this$maskOptions$mas;
      return this.isCharacterFillingPosition(character, position) || ((_this$maskOptions$mas = this.maskOptions.maskPlaceholder) == null ? void 0 : _this$maskOptions$mas[position]) === character;
    };
    this.isCharacterFillingPosition = (character, position) => {
      const {
        mask
      } = this.maskOptions;
      if (!character || position >= mask.length) {
        return false;
      }
      if (!this.isPositionEditable(position)) {
        return (mask == null ? void 0 : mask[position]) === character;
      }
      return new RegExp(mask[position]).test(character);
    };
    this.isPositionEditable = position => {
      const {
        mask,
        permanents
      } = this.maskOptions;
      return position < mask.length && permanents.indexOf(position) === -1;
    };
    this.isValueEmpty = value => value.split('').every((character, position) => !this.isPositionEditable(position) || !this.isCharacterFillingPosition(character, position));
    this.isValueFilled = value => this.getFilledLength(value) === this.maskOptions.lastEditablePosition + 1;
    this.getDefaultSelectionForValue = value => {
      const filledLength = this.getFilledLength(value);
      const cursorPosition = this.getRightEditablePosition(filledLength);
      return {
        start: cursorPosition,
        end: cursorPosition,
        length: 0
      };
    };
    this.getFilledLength = value => {
      const characters = value == null ? void 0 : value.split('');
      const lastFilledIndex = findLastIndex(characters, (character, position) => this.isPositionEditable(position) && this.isCharacterFillingPosition(character, position));
      return lastFilledIndex + 1;
    };
    this.getStringFillingLengthAtPosition = (string, position) => {
      const characters = string.split('');
      const insertedValue = characters.reduce((value, character) => this.insertCharacterAtPosition(value, character, value.length), repeat(' ', position));
      return insertedValue.length - position;
    };
    this.getLeftEditablePosition = position => {
      for (let i = position; i >= 0; i -= 1) {
        if (this.isPositionEditable(i)) {
          return i;
        }
      }
      return null;
    };
    this.getRightEditablePosition = position => {
      for (let i = position || 0; i < this.maskOptions.mask.length; i += 1) {
        if (this.isPositionEditable(i)) {
          return i;
        }
      }
      return null;
    };
    this.formatValue = value => {
      const {
        maskPlaceholder,
        mask
      } = this.maskOptions;
      if (!maskPlaceholder) {
        value = this.insertStringAtPosition('', value, 0);
        while (value.length < mask.length && !this.isPositionEditable(value.length)) {
          value += mask == null ? void 0 : mask[value.length];
        }
        return value;
      }
      return this.insertStringAtPosition(maskPlaceholder, value, 0);
    };
    this.clearRange = (value, start, len) => {
      if (!len) {
        return value;
      }
      const end = start + len;
      const {
        maskPlaceholder,
        mask
      } = this.maskOptions;
      const clearedValue = value.split('').map((character, i) => {
        const isEditable = this.isPositionEditable(i);
        if (!maskPlaceholder && i >= end && !isEditable) return '';
        if (i < start || i >= end) return character;
        if (!isEditable) return mask == null ? void 0 : mask[i];
        if (maskPlaceholder) return maskPlaceholder[i];
        return '';
      }).join('');
      return this.formatValue(clearedValue);
    };
    this.insertCharacterAtPosition = (value, character, position) => {
      const {
        mask,
        maskPlaceholder
      } = this.maskOptions;
      if (position >= mask.length) {
        return value;
      }
      const isAllowed = this.isCharacterAllowedAtPosition(character, position);
      const isEditable = this.isPositionEditable(position);
      const nextEditablePosition = this.getRightEditablePosition(position);
      const isNextPlaceholder = maskPlaceholder && nextEditablePosition ? character === maskPlaceholder[nextEditablePosition] : null;
      const valueBefore = value.slice(0, position);
      if (isAllowed || !isEditable) {
        const insertedCharacter = isAllowed ? character : mask == null ? void 0 : mask[position];
        value = valueBefore + insertedCharacter;
      }
      if (!isAllowed && !isEditable && !isNextPlaceholder) {
        value = this.insertCharacterAtPosition(value, character, position + 1);
      }
      return value;
    };
    this.insertStringAtPosition = (value = '', string, position) => {
      const {
        mask,
        maskPlaceholder
      } = this.maskOptions;
      if (!string || position >= mask.length) {
        return value;
      }
      const characters = string.split('');
      const isFixedLength = this.isValueFilled(value) || !!maskPlaceholder;
      const valueAfter = value.slice(position);
      value = characters.reduce((val, character) => this.insertCharacterAtPosition(val, character, val.length), value.slice(0, position));
      if (isFixedLength) {
        value += valueAfter.slice(value.length - position);
      } else if (this.isValueFilled(value)) {
        value += mask == null ? void 0 : mask.slice(value.length).join('');
      } else {
        const editableCharactersAfter = valueAfter.split('').filter((character, i) => this.isPositionEditable(position + i));
        value = editableCharactersAfter.reduce((val, character) => {
          const nextEditablePosition = this.getRightEditablePosition(val.length);
          if (nextEditablePosition === null) {
            return val;
          }
          if (!this.isPositionEditable(val.length)) {
            val += mask == null ? void 0 : mask.slice(val.length, nextEditablePosition).join('');
          }
          return this.insertCharacterAtPosition(val, character, val.length);
        }, value);
      }
      return value;
    };
    this.processChange = (currentState, previousState, isAutofill) => {
      const {
        mask,
        prefix,
        lastEditablePosition
      } = this.maskOptions;
      const {
        value,
        selection
      } = currentState;
      const {
        value: previousValue,
        selection: previousSelection
      } = previousState;
      let newValue = value;
      let enteredString = '';
      let formattedEnteredStringLength = 0;
      let removedLength = 0;
      let cursorPosition = Math.min(previousSelection == null ? void 0 : previousSelection.start, selection == null ? void 0 : selection.start);
      if (isAutofill) {
        const formatValued = this.formatValue(value || '');
        return {
          value: formatValued,
          enteredString: value,
          selection: {
            start: formatValued.length,
            end: formatValued.length,
            length: 0
          }
        };
      }
      if ((selection == null ? void 0 : selection.end) > (previousSelection == null ? void 0 : previousSelection.start)) {
        enteredString = newValue.slice(previousSelection == null ? void 0 : previousSelection.start, selection == null ? void 0 : selection.end);
        formattedEnteredStringLength = this.getStringFillingLengthAtPosition(enteredString, cursorPosition);
        if (!formattedEnteredStringLength) {
          removedLength = 0;
        } else {
          removedLength = previousSelection == null ? void 0 : previousSelection.length;
        }
      } else if (previousValue != null && previousValue.length && newValue.length < previousValue.length) {
        removedLength = previousValue.length - newValue.length;
      }
      newValue = previousValue;
      if (removedLength) {
        if (removedLength === 1 && !(previousSelection != null && previousSelection.length)) {
          const deleteFromRight = (previousSelection == null ? void 0 : previousSelection.start) === (selection == null ? void 0 : selection.start);
          cursorPosition = deleteFromRight ? this.getRightEditablePosition(selection == null ? void 0 : selection.start) : this.getLeftEditablePosition(selection == null ? void 0 : selection.start);
        }
        newValue = this.clearRange(newValue, cursorPosition, removedLength);
      }
      newValue = this.insertStringAtPosition(newValue, enteredString, cursorPosition);
      cursorPosition += formattedEnteredStringLength;
      if (cursorPosition >= mask.length) {
        cursorPosition = mask.length;
      } else if (cursorPosition < prefix.length && !formattedEnteredStringLength) {
        cursorPosition = prefix.length;
      } else if (cursorPosition >= prefix.length && lastEditablePosition && cursorPosition < lastEditablePosition && formattedEnteredStringLength) {
        cursorPosition = this.getRightEditablePosition(cursorPosition);
      }
      newValue = this.formatValue(newValue);
      return {
        value: newValue,
        enteredString,
        selection: {
          start: cursorPosition,
          end: cursorPosition,
          length: 0
        }
      };
    };
    this.maskOptions = parseMask(options);
  }
}